import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { CancelToken } from 'axios';
import { log } from '@domain/utils/logger.utils';
import { PropsOnlyChildren } from '@domain/types/prop.types';
import AuthContext from '@domain/auth/auth.context';
import AuthUserType from '@domain/auth/types/auth.user.type';
import { AuthSignInData, AuthSignInDataSchema } from '@domain/auth/types/auth.sign.in.data.type';
import { AuthSignInResponse } from '@domain/auth/types/auth.sign.in.response.type';
import { TokenData } from '@domain/auth/types/token.data.type';
import { getUserData, setUserData, clearUserData } from '@domain/auth/auth.storage';
import { useGame } from '@domain/game.selection/game.selection.hook';
import { createRemoteServices } from '@domain/services/remote/remote.services';

export const AuthProvider = ({ children }: PropsOnlyChildren) => {
  const history = useHistory();
  const game = useGame();

  function getUser() {
    const loggedUser = getUserData(game);

    // check token expiration date
    if (!loggedUser || loggedUser.expiration * 1000 <= Date.now()) return null;

    return loggedUser;
  }

  const [user, setUser] = useState<AuthUserType | null>(getUser);
  const authToken = user?.token;

  useEffect(() => {
    setUser(getUser());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game]);

  const authorizedServices = useMemo(() => {
    return createRemoteServices(game, authToken, () => {
      clearUserData(game);
      setUser(null);
    });
  }, [game, authToken]);

  const signIn = useCallback(
    async (data: AuthSignInData, cancelToken: CancelToken | undefined) => {
      log('AuthProvider', 'signIn');

      await AuthSignInDataSchema.validate(data, { abortEarly: false });

      if (cancelToken) cancelToken.throwIfRequested();

      // if we choose not to be remembered, the session will only work for the current tab,
      // but we always ask for a token that will last longer
      const { remember } = data;
      const finalData: AuthSignInData = { ...data, remember: true };

      const response = await authorizedServices.post<AuthSignInResponse>('/auth-token', finalData, { cancelToken });

      const tokenData = jwtDecode<TokenData>(response.data.token);
      const loggedUser: AuthUserType = { username: tokenData.sub, roles: tokenData.roles, token: response.data.token, expiration: tokenData.exp };

      clearUserData(game);
      setUserData(game, remember, loggedUser);
      setUser(loggedUser);

      return loggedUser;
    },
    [game, authorizedServices],
  );

  const signOut = useCallback(() => {
    clearUserData(game);
    setUser(null);

    history.push(`/${game.urlId}`);
  }, [game, history]);

  return <AuthContext.Provider value={{ user, authorizedServices, signIn, signOut }}>{children}</AuthContext.Provider>;
};
